import { faUserSlash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Avatar, Box, Flex } from 'components';
import { IGenericModal, Modal } from 'components/Modal/Modal';
import { P } from 'components/Typography/Typography';
import { Button, Select, UserSelect } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { IUserSelectOption } from 'utilities/userSelectOption';
import {
  Control,
  Controller,
  UseFormSetValue,
  UseFormWatch
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { ITeamOption, TaskForm } from '../TaskFormModal';
import { useEffect, useState } from 'react';
import { useUserFunctionsQuery } from 'hooks/queries/useUserFunctionsQuery';
import {
  filterUserFunctionsByAreaIds,
  mapUserFunctionsToOptions
} from 'utilities/userFunctions';
import { getTeamMembers } from 'utilities/teamMembers';

interface Props extends IGenericModal {
  control: Control<TaskForm, any>;
  usersList: IUserSelectOption[];
  teamsList: ITeamOption[];
  watch: UseFormWatch<TaskForm>;
  setValue: UseFormSetValue<TaskForm>;
  saveAndClose: () => void;
}

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0px;
  th,
  td {
    text-align: left;
    box-sizing: border-box;
    border: 1px solid ${({ theme }) => theme.palette.neutral.tableHeader};
    padding: 10px 15px;
  }
`;

export const TaskParticipantsModal = ({
  isOpen,
  onCancelClick,
  control,
  usersList,
  watch,
  setValue,
  saveAndClose,
  teamsList
}: Props) => {
  const [userFunctionsList, setUserFunctionsList] = useState<IOption[]>([]);
  const { data: userFunctionsData = [] } = useUserFunctionsQuery();
  const areaIds = watch('area_ids');

  const { t } = useTranslation();

  useEffect(() => {
    setUserFunctionsList(
      mapUserFunctionsToOptions(
        filterUserFunctionsByAreaIds(userFunctionsData, areaIds ?? [])
      )
    );
  }, [userFunctionsData, areaIds]);

  return (
    <Modal
      gridTemplateColumns="50%"
      isOpen={isOpen}
      onCancelClick={onCancelClick}
      headerTitle={t('tasks.taskParticipants')}
      mainButton={{
        action: saveAndClose,
        variant: 'eucalyptus',
        label: t('buttons.saveAndClose')
      }}
      isSecondButtonVisible
    >
      <Flex flexDirection="column">
        <Flex>
          <Flex p={1} width="50%" flexDirection="column">
            <Controller
              control={control}
              name="person_in_charge_id"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  label={t('tasks.responsible')}
                  selectedOptions={usersList.find(
                    (userOption) => userOption.value === value
                  )}
                  onChange={(val: IOption) => {
                    onChange(val.value);
                  }}
                  options={usersList}
                />
              )}
            />
            <Controller
              control={control}
              name="team_leader_id"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  label={t('tasks.teamLeader')}
                  selectedOptions={usersList.find(
                    (userOption) => userOption.value === value
                  )}
                  onChange={(val: IOption) => {
                    onChange(val.value);
                  }}
                  options={usersList}
                />
              )}
            />

            <Controller
              control={control}
              name="task_participations_attributes"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  isMulti
                  hideElements
                  label={t('tasks.addTeamMember')}
                  selectedOptions={value}
                  onChange={(val: IOption[]) => {
                    onChange(val);
                  }}
                  options={usersList}
                />
              )}
            />
          </Flex>

          <Flex p={1} width="50%" flexDirection="column">
            <Controller
              control={control}
              name="author_id"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  disabled
                  label={t('tasks.author')}
                  selectedOptions={usersList.find(
                    (userOption) => userOption.value === value
                  )}
                  onChange={(val: IOption) => {
                    onChange(val.value);
                  }}
                  options={usersList}
                />
              )}
            />
            <Controller
              control={control}
              name="team_manager_id"
              render={({ field: { onChange, value } }) => (
                <UserSelect
                  label={t('tasks.teamManager')}
                  selectedOptions={usersList.find(
                    (userOption) => userOption.value === value
                  )}
                  onChange={(val: IOption) => {
                    onChange(val.value);
                  }}
                  options={usersList}
                />
              )}
            />
            <Controller
              control={control}
              name="task_participations_attributes"
              render={({ field: { onChange, value } }) => (
                <Select
                  isMulti
                  hideElements
                  label={t('tasks.addFromTeamOrGroup')}
                  selectedOptions={
                    teamsList.filter((teamItem) =>
                      teamItem.members.every((teamMember) => {
                        !!value?.find(
                          (taskParticipationAttribute) =>
                            taskParticipationAttribute.value ===
                            teamMember.id?.toString()
                        );
                      })
                    ) || []
                  }
                  onChange={(val: ITeamOption[]) => {
                    const newParticipants = [
                      ...(value || []),
                      ...(val || [])
                        .map((team) =>
                          getTeamMembers(team).map((member) => ({
                            value: member.id,
                            label: `${member.first_name || '-'} ${
                              member.last_name || '-'
                            }`
                          }))
                        )
                        .flat()
                    ].filter(
                      // remove duplicates
                      (value, index, self) =>
                        index === self.findIndex((t) => t.value === value.value)
                    );

                    onChange(newParticipants);
                  }}
                  options={teamsList}
                />
              )}
            />
          </Flex>
        </Flex>

        <Flex maxHeight={280} overflow="auto">
          <StyledTable>
            <thead>
              <th>
                <P variant="body">{t('tasks.teamMembers')}</P>
              </th>
              <th>
                <P variant="body">{t('tasks.role/function')}</P>
              </th>
              <th></th>
            </thead>
            <tbody>
              {watch('task_participations_attributes')?.map(
                (taskParticipationAttribute) => (
                  <tr key={taskParticipationAttribute.value}>
                    <td>
                      <Flex alignItems="center">
                        <Box mr={2}>
                          <Avatar size="extra-small" />
                        </Box>
                        <P variant="body">{taskParticipationAttribute.label}</P>
                      </Flex>
                    </td>
                    <td align="center">
                      <Flex mt={2}>
                        <Select
                          selectedOptions={userFunctionsList.find(
                            (functionItem) =>
                              functionItem.value ===
                              taskParticipationAttribute.function_id
                          )}
                          onChange={(val: IOption) => {
                            setValue(
                              'task_participations_attributes',
                              watch('task_participations_attributes')?.map(
                                (mapTaskParticipationAttribute) =>
                                  mapTaskParticipationAttribute.value ===
                                  taskParticipationAttribute.value
                                    ? {
                                        ...mapTaskParticipationAttribute,
                                        function_id: val.value
                                      }
                                    : mapTaskParticipationAttribute
                              ) || []
                            );
                          }}
                          options={userFunctionsList}
                        />
                      </Flex>
                    </td>
                    <td>
                      <Button
                        variant="red"
                        icon={<FontAwesomeIcon icon={faUserSlash} />}
                        onClick={() => {
                          setValue(
                            'task_participations_attributes',
                            watch('task_participations_attributes')?.filter(
                              (filterTaskParticipationsAttribute) =>
                                filterTaskParticipationsAttribute.value !==
                                taskParticipationAttribute.value
                            ) || []
                          );
                        }}
                      />
                    </td>
                  </tr>
                )
              )}
            </tbody>
          </StyledTable>
        </Flex>
      </Flex>
    </Modal>
  );
};
